import Application from "../../Application";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IOrganisation } from "../../organisations/model/Organisation";
import { ILegalView } from "../model/LegalView";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import { IUser } from "../../user/model/User";
import GetOrganisationLegalViewTypesInteractor from "../../organisations/interactors/GetOrganisationLegalViewTypesInteractor";
import CreateLegalViewInteractor from "../interactor/CreateLegalViewInteractor";
import GetLegalViewTypeIndexDataInteractor from "../interactor/GetLegalViewTypeIndexDataInteractor";
import SummaryTranslation from "../../core/model/translations/SummaryTranslation";
import { TDocumentTypeIndexCategoryPresentationModel } from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import DocumentTypeIndexCategoryPresentationModel from "../../documentTypes/presenter/DocumentTypeIndexCategoryPresentationModel";

export interface TLegalViewCreationPresenter extends TLoadingAwarePresenter {
  organisations: IOrganisation[];
  selectedOrganisationId: number;
  legalViewTypes: IDocumentType[];
  organisationIndexes: TDocumentTypeIndexCategoryPresentationModel[];
  legalViewTypeId?: number;
  user?: IUser;
}
export interface ILegalViewCreationPresenter
  extends TLegalViewCreationPresenter,
    TPresentable {
  loadReferenceValues(organisations: IOrganisation[]): void;
  setOrganisationId(organisationId: number): void;
  createLegalView(legalView: ILegalView): void;
  showLegalViewList(): void;
  setLegalViewTypeId(legalViewTypeId: number): void;
}
const defaultState: TLegalViewCreationPresenter = {
  organisations: [],
  selectedOrganisationId: NaN,
  legalViewTypes: [],
  organisationIndexes: []
};
const LegalViewCreationPresenter = withStore<
  ILegalViewCreationPresenter,
  TLegalViewCreationPresenter
>(({ application, store, loader, translate }): ILegalViewCreationPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TLegalViewCreationPresenter>();

  const loadReferenceValues = (organisations: IOrganisation[]) => {
    const user = _store.getState<any>().user;
    if (user && user.isOperator()) {
      setOrganisationId(user.organisationId);
    }
    _store.update({
      organisations,
      user
    });
  };
  const getLegalViewTypeIndexes = async (legalViewTypeId: number) => {
    const { selectedLanguage, selectedOrganisationId } = _store.getState<any>();
    let organisationIndexes = await _application.container
      .resolve<GetLegalViewTypeIndexDataInteractor>("getLegalViewTypeIndexData")
      .execute(legalViewTypeId, selectedOrganisationId);

    _store.update({
      organisationIndexes: organisationIndexes.map((item) =>
        DocumentTypeIndexCategoryPresentationModel(item, selectedLanguage)
      )
    });
  };

  const getOrganisationLegalViewTypes = async (organisationId: number) => {
    loader.start("organisationLoader");
    let legalViewTypes = await _application.container
      .resolve<GetOrganisationLegalViewTypesInteractor>(
        "getOrganisationLegalViewTypes"
      )
      .execute(organisationId);
    _store.update({
      legalViewTypes
    });
    if (legalViewTypes.length === 1) {
      setLegalViewTypeId(legalViewTypes[0].id);
    }
    loader.stop("organisationLoader");
  };
  const setLegalViewTypeId = async (legalViewTypeId: number) => {
    await getLegalViewTypeIndexes(legalViewTypeId);
    _store.update({
      legalViewTypeId: legalViewTypeId
    });
  };

  const setOrganisationId = async (organisationId: number) => {
    _store.update({ selectedOrganisationId: organisationId });
    await getOrganisationLegalViewTypes(organisationId);
  };

  const createLegalView = async (legalView: any) => {
    loader.start("legalViewLoader");

    const { selectedLanguage } = _store.getState<any>();
    let elementList =
      legalView.organisationIndexList &&
      legalView.organisationIndexList.length > 0
        ? legalView.organisationIndexList.flat(1)
        : [];
    let translations = legalView.summaryTranslations.map((item) => {
      var element = SummaryTranslation(item);
      element.organisationId = legalView.organisationId;
      return element;
    });

    let originalSummary = translations?.find(
      (item) => item.languageId === selectedLanguage.id
    );
    legalView.summary = originalSummary?.summaryTranslation ?? "";
    legalView.note = originalSummary?.noteTranslation ?? "";
    legalView.summaryTranslations = translations;
    let record = await _application.container
      .resolve<CreateLegalViewInteractor>("createLegalView")
      .execute(
        legalView,
        elementList.filter((item) => item !== null && item !== undefined)
      );
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("editors.project.create_success_message"));
    loader.stop("legalViewLoader");
    showLegalViewDetails(record.id);
    _store.update({ selectedCourtCases: [] });
    return true;
  };

  const showLegalViewDetails = (id: number) => {
    return _application.navigator.push({
      pathname: `/legal-views/${id.toString()}/details`
    });
  };
  const showLegalViewList = () => {
    return _application.navigator.push({
      pathname: `/legal-views`
    });
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadReferenceValues,
    setOrganisationId,
    createLegalView,
    showLegalViewList,
    setLegalViewTypeId
  };
}, defaultState);
export default LegalViewCreationPresenter;
